import React, { FC, Fragment, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Grid } from '$components/layouts';
import { useTranslation } from '$hooks';
import { PersonRoles } from '~/constants';
import { IEnrichedProductPerson, IEnrichedProductSerie, IProduct } from '$models';
import { Link } from '$components/elements/link';
import { isTruthyNumber } from '$lib/helpers';

type Props = {
    product: IProduct;
    series?: IEnrichedProductSerie[];
    persons?: IEnrichedProductPerson[];
};

export const BookDetails: FC<Props> = ({ product, series, persons }: Props): JSX.Element => {
    const [authors, setAuthors] = useState<IEnrichedProductPerson[]>([]);
    const [translators, setTranslators] = useState<IEnrichedProductPerson[]>([]);
    const { translate } = useTranslation();

    useEffect(() => {
        setTranslators(
            persons?.filter((person: IEnrichedProductPerson) =>
                person?.personTypes?.some((type) => type === PersonRoles.Translator)
            ) || []
        );
        setAuthors(
            persons?.filter((person: IEnrichedProductPerson) =>
                person?.personTypes?.some((type) => type === PersonRoles.Author)
            ) || []
        );
    }, [persons]);

    return (
        <StyledGrid flow="row" columns="1fr 1fr">
            {!!authors?.filter((author) => !!author.personName).length && (
                <>
                    <div>
                        {authors.length === 1
                            ? translate('productPage.bookDescription.authorLabelSingle')
                            : translate('productPage.bookDescription.authorLabel')}
                    </div>
                    <div>
                        {authors
                            ?.filter((author) => !!author.personName)
                            .map((author: IEnrichedProductPerson, index: number) => (
                                <Fragment key={index}>
                                    <Link themedLink href={author?.personUrl}>
                                        {author?.personName}
                                    </Link>
                                    {index < (authors.length || 0) - 2 && <span>{', '}</span>}
                                    {index === (authors.length || 0) - 2 && (
                                        <span>{` ${translate('person.details.and')} `}</span>
                                    )}
                                </Fragment>
                            ))}
                    </div>
                </>
            )}
            {!!series?.length && (
                <>
                    <div>
                        {series.length === 1
                            ? translate('productPage.bookDescription.seriesLabelSingle')
                            : translate('productPage.bookDescription.seriesLabel')}
                    </div>
                    <div>
                        {series.map((serie: IEnrichedProductSerie, index: number) => (
                            <Fragment key={index}>
                                <Link themedLink href={serie?.url}>
                                    {serie?.title}
                                </Link>
                                {index < (product.series?.length || 0) - 1 ? (
                                    <span>{` ${translate('productPage.productDescription.and')} `}</span>
                                ) : (
                                    <></>
                                )}
                            </Fragment>
                        ))}
                    </div>
                </>
            )}
            {!!translators.length && (
                <>
                    <div>
                        {translators.length === 1
                            ? translate('productPage.bookDescription.translatorLabelSingle')
                            : translate('productPage.bookDescription.translatorLabel')}
                    </div>
                    <div>
                        {translators.map((translator: IEnrichedProductPerson, index: number) => (
                            <Fragment key={index}>
                                <Link themedLink href={translator?.personUrl}>
                                    {translator?.personName}
                                </Link>
                                {index < translators.length - 1 ? (
                                    <span>{` ${translate('productPage.productDescription.and')} `}</span>
                                ) : (
                                    <></>
                                )}
                            </Fragment>
                        ))}
                    </div>
                </>
            )}
            {product.itemPublisher && (
                <>
                    <div>{translate('productPage.bookDescription.itemPublisherLabel')}</div>
                    <div>{product.itemPublisher}</div>
                </>
            )}
            {product.bookDetails?.itemIssue && (
                <>
                    <div>{translate('productPage.bookDescription.itemIssueLabel')}</div>
                    <div>{product.bookDetails?.itemIssue}</div>
                </>
            )}
            {product.itemIsbn && (
                <>
                    <div>{translate('productPage.bookDescription.itemIsbnLabel')}</div>
                    <div>{product.itemIsbn}</div>
                </>
            )}
            {product.itemLanguage && (
                <>
                    <div>{translate('productPage.bookDescription.itemLanguageLabel')}</div>
                    <div>{product.itemLanguage}</div>
                </>
            )}
            {product.productOriginalLanguage && (
                <>
                    <div>{translate('productPage.bookDescription.productOriginalLanguageLabel')}</div>
                    <div>{product.productOriginalLanguage}</div>
                </>
            )}
            {product.itemOriginalTitle && (
                <>
                    <div>{translate('productPage.bookDescription.itemOriginalTitleLabel')}</div>
                    <div>{product.itemOriginalTitle}</div>
                </>
            )}
            {!!product.itemNumberOfPages && (
                <>
                    <div>{translate('productPage.bookDescription.itemNumberOfPagesLabel')}</div>
                    <div>{product.itemNumberOfPages}</div>
                </>
            )}
            {product.itemReleaseDate && (
                <>
                    <div>{translate('productPage.bookDescription.itemReleaseDateLabel')}</div>
                    <div>
                        {product.itemReleaseDate.length > 10
                            ? product.itemReleaseDate.slice(0, 10)
                            : product.itemReleaseDate}
                    </div>
                </>
            )}
            {product.bookDetails?.itemGlobalBinding && (
                <>
                    <div>{translate('productPage.bookDescription.itemGlobalBindingLabel')}</div>
                    <div>{product.bookDetails?.itemGlobalBinding}</div>
                </>
            )}
            {product.itemPublizonBookFormat && (
                <>
                    <div>{translate('productPage.bookDescription.itemPublizonBookFormatLabel')}</div>
                    <div>{product.itemPublizonBookFormat}</div>
                </>
            )}
            {product.bookDetails?.itemFileType && (
                <>
                    <div>{translate('productPage.bookDescription.itemFIleTypeLabel')}</div>
                    <div>{product.bookDetails?.itemFileType}</div>
                </>
            )}
            {product.bookDetails?.itemDRM && (
                <>
                    <div>{translate('productPage.bookDescription.itemDRMLabel')}</div>
                    <div>{product.bookDetails?.itemDRM}</div>
                </>
            )}
            {isTruthyNumber(product.itemNumbersOfMedia) && (
                <>
                    <div>{translate('productPage.bookDescription.itemNumbersOfMediaLabel')}</div>
                    <div>{product.itemNumbersOfMedia}</div>
                </>
            )}
            {product.bookDetails?.itemDataSize && (
                <>
                    <div>{translate('productPage.bookDescription.itemDataSizeLabel')}</div>
                    <div>{product.bookDetails?.itemDataSize}</div>
                </>
            )}
            {product.itemNumber && (
                <>
                    <div>{translate('productPage.bookDescription.itemNumberLabel')}</div>
                    <div>{product.itemNumber}</div>
                </>
            )}
            {product.bookDetails?.itemEanCode && (
                <>
                    <div>{translate('productPage.bookDescription.itemEanCodeLabel')}</div>
                    <div>{product.bookDetails?.itemEanCode}</div>
                </>
            )}
            {product.productBrand && (
                <>
                    <div>{translate('productPage.bookDescription.productBrandLabel')}</div>
                    <div>{product.productBrand}</div>
                </>
            )}
            {product.itemGroupName && (
                <>
                    <div>{translate('productPage.bookDescription.itemGroupNameLabel')}</div>
                    <div>{product.itemGroupName}</div>
                </>
            )}
            {product.itemCountryOfOrigin && (
                <>
                    <div>{translate('productPage.bookDescription.itemCountryOfOriginLabel')}</div>
                    <div>{product.itemCountryOfOrigin}</div>
                </>
            )}
            {product.nonBookDetails?.productAgeGroup && (
                <>
                    <div>{translate('productPage.bookDescription.productAgeGroupLabel')}</div>
                    <div>{product.nonBookDetails?.productAgeGroup}</div>
                </>
            )}
            {isTruthyNumber(product.itemHeight) && (
                <>
                    <div>{translate('productPage.bookDescription.itemHeightLabel')}</div>
                    <div>{product.itemHeight}</div>
                </>
            )}
            {isTruthyNumber(product.itemWidth) && (
                <>
                    <div>{translate('productPage.bookDescription.itemWidthLabel')}</div>
                    <div>{product.itemWidth}</div>
                </>
            )}
            {isTruthyNumber(product.itemLength) && (
                <>
                    <div>{translate('productPage.bookDescription.itemLengthLabel')}</div>
                    <div>{product.itemLength}</div>
                </>
            )}
            {isTruthyNumber(product.itemWeight) && (
                <>
                    <div>{translate('productPage.bookDescription.itemWeightLabel')}</div>
                    <div>{product.itemWeight}</div>
                </>
            )}
        </StyledGrid>
    );
};

const StyledGrid = styled(Grid)(({ theme }) => ({
    '& div': {
        fontSize: theme.fontSizes.sm,
        borderBottom: `1px solid ${theme.colors.primaryGreyLight}`,
        padding: `${theme.space[3]} 0`,
        ':first-child, :nth-child(2)': {
            paddingTop: 0,
        },
    },

    '& div:nth-child(even)': {
        textAlign: 'right',
    },
}));
